import { ActionIcon, Anchor, Badge, Card, Group, Paper, SimpleGrid, Table, Text, Title, Tooltip } from '@mantine/core';
import { IconArrowLeft, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import queryString from 'query-string';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import ErrorMessage from '../../../components/ErrorMessage';
import Metadata from '../../../components/Metadata';
import MetricsSelector from '../../../components/MetricsSelector';
import Pager from '../../../components/Pager/Pager';
import Spinner from '../../../components/Spinner';
import PageSizeOptions from '../../../constants/pageSizeOptions';
import { Page } from '../../../constants/pageTitles';
import { EM_DASH, formatCount, formatCurrency } from '../../../helpers/atAGlance';
import {
  describeAttribution,
  describeRefundStatus,
  profileHref,
  profileLabel,
} from '../../../helpers/attributedPurchases';
import { dateToEndOfDay, dateToStartOfDay, daysAgoISODate, todayISODate } from '../../../helpers/dateUtils';
import getErrorMessage from '../../../helpers/getErrorMessage';
import useFetch from '../../../helpers/useFetch';

// Matches the dashboard's own default, so arriving here without a range in the
// URL shows the same period the tab that links here would have been showing.
const DEFAULT_PERIOD_DAYS = 14;
// 20, not the app's usual default of PageSizeOptions[0] (10) — an order's
// product breakdown is read one order at a time, so a denser page costs less
// scrolling than it would on a row-per-click list. Looked up by value rather
// than position, so reordering pageSizeOptions.jsx cannot silently move it.
const DEFAULT_PAGE_SIZE = PageSizeOptions.includes(20) ? 20 : PageSizeOptions[0];

const formatMoment = (timestamp) => DateTime.fromISO(timestamp).toFormat('d LLL yyyy, HH:mm');

// MetricTile's shape without its delta line. This page has no previous period
// to compare against — it is a list of individual orders, not a periodic metric
// — and MetricTile always draws a change indicator, which against a missing
// previous value reads as a confident "+100% vs previous period".
const SummaryTile = ({ label, value, hint }) => (
  <Paper withBorder radius="md" p="md" role="group" aria-label={label}>
    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
      {label}
    </Text>
    <Text fz={22} fw={700} mt={4} lh={1.2}>
      {value}
    </Text>
    {hint && (
      <Text size="xs" c="dimmed">
        {hint}
      </Text>
    )}
  </Paper>
);

// An order line's components, drawn as one badge each. A line really can carry
// several — the same product recommended by a block AND clicked through from a
// campaign email — and every one of them is credited for its full value, so the
// badges are a list rather than a single winner.
const AttributionBadges = ({ attributions, accountId, domainId }) => {
  if (attributions.length === 0) {
    return (
      <Tooltip label="No Releva surface was credited for this line, so its value is not in the attributed total">
        <Text size="xs" c="dimmed">
          Not attributed
        </Text>
      </Tooltip>
    );
  }

  return (
    <Group gap={6} wrap="wrap">
      {attributions.map((attribution, index) => {
        const { label, name, href } = describeAttribution({ accountId, domainId, attribution });
        // The kind always shows; the instance only when it can still be named.
        // A component deleted since it earned this is still why the order is on
        // the list, so the badge stays and says what kind of thing it was.
        const text = name ? `${label}: ${name}` : label;

        return (
          <Badge
            key={`${attribution.channel}-${attribution.id}-${index}`}
            variant="light"
            color={href ? 'primary' : 'gray'}
            size="sm"
            style={{ textTransform: 'none' }}
          >
            {href ? (
              <Anchor href={href} size="xs" inherit>
                {text}
              </Anchor>
            ) : (
              text
            )}
          </Badge>
        );
      })}
    </Group>
  );
};

// One order's refund status. A refunded order still belongs on this list — its
// revenue is in the card's attributed figure, which counts purchases and nets
// refunds separately — but a reader deciding what a campaign earned needs to see
// that some of it came back.
const RefundStatus = ({ purchase, currency }) => {
  const status = describeRefundStatus(purchase);

  // Both causes, because the page cannot tell them apart and naming only the
  // first would be flatly wrong on a domain that records no refund event at all
  // — where every row lands here.
  if (!status) {
    return (
      <Tooltip label="Unknown: either this order carries no id to match a refund against, or this domain records no refund event">
        <Text size="sm" c="dimmed">
          {EM_DASH}
        </Text>
      </Tooltip>
    );
  }

  // A clean order is the common case: leave the cell empty and let the refunded
  // rows stand out, rather than labelling every row of the list.
  if (purchase.status === 'notRefunded') {
    return null;
  }

  const badge = (
    <Badge color={status.color} variant={status.variant} size="sm" style={{ textTransform: 'none' }}>
      {status.label}
    </Badge>
  );

  // Only the refunded states carry an amount worth stating. "No refund" with a
  // tooltip saying 0 came back would be noise on most rows of most lists.
  if (purchase.refundedValue > 0) {
    return <Tooltip label={`${formatCurrency(purchase.refundedValue, currency)} refunded`}>{badge}</Tooltip>;
  }

  return badge;
};

// One order: the summary row, and the per-product breakdown it opens into.
//
// Collapsed by default. The breakdown is the point of the page, but a reader
// arrives looking for one order among many, and a page that opens every line of
// every order at once is one they have to scroll past rather than read.
const PurchaseRow = ({ purchase, accountId, domainId, currency, columnCount }) => {
  const [open, setOpen] = useState(false);
  const href = profileHref({ accountId, domainId, profileId: purchase.profileId });
  const label = profileLabel(purchase);

  // The distinct kinds of component this order was credited to, so the collapsed
  // row still answers "what did Releva do here" without being opened.
  const channels = [];
  for (const product of purchase.products) {
    for (const attribution of product.attributions) {
      const { label: channelLabel } = describeAttribution({ accountId, domainId, attribution });
      if (!channels.includes(channelLabel)) {
        channels.push(channelLabel);
      }
    }
  }

  return (
    <>
      <Table.Tr>
        <Table.Td w={40}>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => setOpen((wasOpen) => !wasOpen)}
            aria-label={open ? 'Hide products' : 'Show products'}
            aria-expanded={open}
          >
            {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          </ActionIcon>
        </Table.Td>
        <Table.Td style={{ whiteSpace: 'nowrap' }}>
          <Text size="sm">{formatMoment(purchase.timestamp)}</Text>
          <Text size="xs" c="dimmed">
            {purchase.action}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{purchase.orderId || EM_DASH}</Text>
        </Table.Td>
        <Table.Td>
          <RefundStatus purchase={purchase} currency={currency} />
        </Table.Td>
        <Table.Td>
          {/* The order is listed whether or not the profile survives: its revenue
              is in the total this page explains either way. Without a profile
              there is nothing to open, so the id reads as plain text. */}
          {purchase.profile && href ? (
            <Anchor size="sm" href={href}>
              {label}
            </Anchor>
          ) : (
            <Tooltip
              label={
                // A profileId with no profile is one that existed and was deleted
                // or merged away since; no profileId at all means the order never
                // carried one, which is a different claim.
                purchase.profileId
                  ? 'This profile has been deleted or merged away'
                  : 'This order carries no profile'
              }
            >
              <Text size="sm" c="dimmed">
                {label || EM_DASH}
              </Text>
            </Tooltip>
          )}
        </Table.Td>
        <Table.Td>
          <Text size="sm" fw={600}>
            {formatCurrency(purchase.attributedValue, currency)}
          </Text>
          <Text size="xs" c="dimmed">
            of {formatCurrency(purchase.orderValue, currency)}
          </Text>
        </Table.Td>
        <Table.Td>
          <Group gap={6} wrap="wrap">
            {channels.map((channel) => (
              <Badge key={channel} variant="outline" size="sm" style={{ textTransform: 'none' }}>
                {channel}
              </Badge>
            ))}
          </Group>
        </Table.Td>
      </Table.Tr>
      {/* Rendered only while open, rather than hidden with a Collapse. A page
          of 25 orders would otherwise mount every line of every one of them on
          load, for rows nobody has asked to see — and an animated height is not
          worth that on a table whose job is to be read. */}
      {open && (
        <Table.Tr>
          {/* One cell spanning the row, so the nested table lines up under the
              summary instead of inheriting its column widths. */}
          <Table.Td colSpan={columnCount} p={0}>
            {/* An order id is nullable, and `Products in order null` is what
                naming the table unconditionally would announce. */}
            <Table
              withColumnBorders={false}
              verticalSpacing="xs"
              aria-label={purchase.orderId ? `Products in order ${purchase.orderId}` : 'Products in this order'}
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th pl="xl">Product</Table.Th>
                  <Table.Th w={120}>SKU</Table.Th>
                  <Table.Th w={80}>Quantity</Table.Th>
                  <Table.Th w={120}>Value</Table.Th>
                  <Table.Th>Attributed to</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {purchase.products.map((product, index) => (
                  <Table.Tr key={`${product.id || 'line'}-${index}`}>
                    <Table.Td pl="xl">
                      {/* An order can carry a line with no product at all: an
                          event that reported a value but no catalogue items gets
                          one synthetic, id-less line standing in for the whole
                          order. Naming that honestly beats an empty cell. */}
                      {product.id ? (
                        <Anchor
                          size="sm"
                          href={`#/accounts/${accountId}/domains/${domainId}/products/${encodeURIComponent(product.id)}`}
                        >
                          {product.name || product.id}
                        </Anchor>
                      ) : (
                        <Text size="sm" c="dimmed">
                          Order value, no product reported
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{product.sku || EM_DASH}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatCount(product.quantity)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatCurrency(product.value, currency)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <AttributionBadges
                        attributions={product.attributions}
                        accountId={accountId}
                        domainId={domainId}
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
};

// The orders behind the At a Glance tab's attributed-revenue card: which ones
// Releva was credited for, what was bought, and which surface earned each line.
const AttributedPurchasesPage = () => {
  const fetch = useFetch();
  const location = useLocation();
  const navigate = useNavigate();
  const { domain } = useSelector((state) => state.domain);

  const search = useMemo(() => queryString.parse(location.search), [location.search]);
  // The range lives in the URL so this page is linkable and survives a reload —
  // the dashboard keeps its own range in component state, which is exactly why
  // the link that brings a reader here has to carry it.
  const startDate = search.startDate || daysAgoISODate(DEFAULT_PERIOD_DAYS - 1);
  const endDate = search.endDate || todayISODate();
  const size = Math.max(parseInt(search.size, 10) || DEFAULT_PAGE_SIZE, 1);
  const from = Math.max(parseInt(search.from, 10) || 0, 0);

  const [data, setData] = useState();
  const [error, setError] = useState();

  const setSearch = (changes) =>
    navigate({ search: queryString.stringify({ startDate, endDate, from, size, ...changes }) });

  useEffect(() => {
    let ignore = false;
    setData(undefined);
    setError(undefined);

    const url =
      `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/atAGlance/${encodeURIComponent(
        dateToStartOfDay(startDate, 'local')
      )}/${encodeURIComponent(dateToEndOfDay(endDate, 'local'))}/attributedPurchases?` +
      queryString.stringify({ from, size });

    fetch(url)
      .then((response) => {
        if (!ignore) {
          setData(response);
        }
      })
      .catch((err) => {
        if (!ignore) {
          setError(err);
        }
      });

    return () => {
      ignore = true;
    };
  }, [domain.accountId, domain.id, startDate, endDate, from, size, fetch]);

  const currency = domain.currency;
  const dashboardHref = `#/accounts/${domain.accountId}/domains/${domain.id}/analytics?mainTab=atAGlance`;

  const header = (
    <>
      <Metadata title={`${Page['dashboard']} | Attributed orders`} />
      <Group justify="space-between" align="flex-end" mb="md" wrap="wrap">
        <div>
          <Anchor href={dashboardHref} size="sm">
            <Group gap={4} wrap="nowrap">
              <IconArrowLeft size={14} />
              Back to At a Glance
            </Group>
          </Anchor>
          <Title order={4} mt="xs">
            Orders attributed to Releva
          </Title>
        </div>
        <MetricsSelector
          startDate={startDate}
          endDate={endDate}
          // Changing the range changes which orders exist, so page 7 of the old
          // range is not page 7 of the new one. Both setters reset the offset
          // rather than leaving the reader on an offset past the end of a
          // shorter list, looking at an empty table.
          setStartDate={(value) => setSearch({ startDate: value, from: 0 })}
          setEndDate={(value) => setSearch({ endDate: value, from: 0 })}
          setDateRange={(start, end) => setSearch({ startDate: start, endDate: end, from: 0 })}
        />
      </Group>
    </>
  );

  if (error) {
    return (
      <>
        {header}
        <ErrorMessage message={getErrorMessage(error)} />
      </>
    );
  }

  if (data === undefined) {
    return (
      <>
        {header}
        <Spinner />
      </>
    );
  }

  const { totals = {}, purchases = [], purchaseActions = [] } = data;
  // The endpoint clamps how far into the list it will page — a deep OFFSET makes
  // ClickHouse produce and discard every skipped row — so the pager stops where
  // the data does. Without this cap it would offer pages past the clamp that all
  // show the same rows. Rounded down to a whole page: the pager walks the list
  // in `size`-sized steps from 0, so a `maxOffset` that `size` does not divide
  // would otherwise still offer one page whose offset lands past the clamp.
  const reachableOrders =
    data.maxOffset == null
      ? totals.orders || 0
      : Math.min(totals.orders || 0, Math.floor(data.maxOffset / size) * size + size);
  const pageCount = Math.ceil(reachableOrders / size);
  // Expand toggle, time, order, status, profile, attributed, credited to.
  const columnCount = 7;

  return (
    <>
      {header}

      <Card withBorder radius="md" padding="md" mb="md">
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          <SummaryTile label="Attributed orders" value={formatCount(totals.orders)} />
          <SummaryTile label="Attributed revenue" value={formatCurrency(totals.attributedValue, currency)} />
          <SummaryTile
            label="Value of those orders"
            value={formatCurrency(totals.orderValue, currency)}
            hint="Including the lines Releva was not credited for"
          />
        </SimpleGrid>
        {/* Two things a reader comparing this with the card they came from will
            otherwise take for a bug. Which events count as a purchase is per
            domain, and refunds are a different event entirely — they net the
            card's figure and are not listed here, so this total can sit above
            it on a domain that refunds. */}
        <Text size="xs" c="dimmed" mt="md">
          Counting {purchaseActions.length ? purchaseActions.join(', ') : 'no event'} as a purchase. These figures are
          what the orders earned: a refund is flagged in the Status column but is not subtracted here, so on a store
          that refunds this reads a little above the At a Glance card, which nets them.
        </Text>
      </Card>

      <Card withBorder radius="md" padding="md" mb="md">
        {purchases.length === 0 ? (
          <Text size="sm" c="dimmed">
            No order in this period was attributed to Releva. Either nothing was bought after a Releva message or
            on-site block, or the events that record it are not reaching us — the Data Quality tab reports the latter.
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={980}>
            <Table striped highlightOnHover aria-label="Orders attributed to Releva">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={40} />
                  <Table.Th>Time</Table.Th>
                  <Table.Th>Order</Table.Th>
                  <Table.Th w={160}>Status</Table.Th>
                  <Table.Th>Profile</Table.Th>
                  <Table.Th w={150}>Attributed</Table.Th>
                  <Table.Th>Credited to</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {purchases.map((purchase, index) => (
                  <PurchaseRow
                    // An order id is not unique — one order attributed through
                    // two profiles, or an id-less order, would collide — so the
                    // position in the page is what keys the row.
                    key={`${purchase.orderId}-${purchase.profileId}-${index}`}
                    purchase={purchase}
                    accountId={domain.accountId}
                    domainId={domain.id}
                    currency={currency}
                    columnCount={columnCount}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      <Pager
        pageSize={size}
        pageCount={pageCount}
        onPageChange={(event) => setSearch({ from: (event.selected - 1) * size })}
        initialPage={Math.floor(from / size) + 1}
        setPageSize={(pageSize) => setSearch({ size: pageSize, from: 0 })}
      />

      {/* Said plainly rather than left as a pager that simply stops: a reader who
          cannot reach an order they know exists should be told why, and told the
          one thing that fixes it. */}
      {reachableOrders < (totals.orders || 0) && (
        <Text size="xs" c="dimmed" ta="right" mb="md">
          Showing the most recent {formatCount(reachableOrders)} of {formatCount(totals.orders)} attributed orders.
          Narrow the date range to reach the rest.
        </Text>
      )}
    </>
  );
};

export default AttributedPurchasesPage;
